/**
 * CallSettingsPanel - Overlay panel for call settings
 * Shows DND toggle, speed dials, call forwarding info, and preferences.
 *
 * Rendered via portal at document.body so it stacks above global Header / CapacitorFooter
 * while matching Total Connect shell styling and safe areas.
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  X,
  Settings,
  Bell,
  BellOff,
  Zap,
  PhoneForwarded,
  Phone,
  Voicemail,
  Smartphone,
  Volume2,
} from 'lucide-react';
import {
  MOBILE_SAFE_AREA_BOTTOM,
  MOBILE_SAFE_AREA_TOP,
} from '@/components/work-items/mobileSafeArea';
import type { CallSettings } from '../types';
import {
  CALL_RINGTONE_OPTIONS,
  getRingtoneOption,
  loadCallSoundPreferences,
  normalizeStoredRingtoneId,
  playCallSound,
} from '@/lib/callSoundPreferences';

export interface CallSettingsPanelProps {
  callSettings: CallSettings | null;
  isDNDEnabled: boolean;
  dndLoading: boolean;
  onToggleDND: () => void;
  onClose: () => void;
  onDialSpeedDial?: (number: string) => void;
  onOpenVoicemail?: () => void;
  isDark?: boolean;
  /** Match TotalConnectRTC chrome accents (buttons, header icon). */
  accentColor?: string;
  /** When set, show SIP inbound mobile-only option (requires a SIP extension). */
  hasSipExtension?: boolean;
  sipInboundMobileOnly?: boolean;
  sipInboundMobileOnlyLoading?: boolean;
  onToggleSipInboundMobileOnly?: () => void;
  /** Persist incoming ringtone + volume to `/api/v1/call-settings` (Total Connect / SIP). */
  onSaveIncomingRingtone?: (ringtoneId: string, volumePercent: number) => Promise<void>;
  /** Remove server override — follow Client Zone profile ringtone. */
  onClearIncomingRingtone?: () => Promise<void>;
}

export function CallSettingsPanel({
  callSettings,
  isDNDEnabled,
  dndLoading,
  onToggleDND,
  onClose,
  onDialSpeedDial,
  onOpenVoicemail,
  isDark = true,
  accentColor,
  hasSipExtension = false,
  sipInboundMobileOnly = false,
  sipInboundMobileOnlyLoading = false,
  onToggleSipInboundMobileOnly,
  onSaveIncomingRingtone,
  onClearIncomingRingtone,
}: CallSettingsPanelProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [incomingToneId, setIncomingToneId] = useState('classic');
  const [incomingToneVol, setIncomingToneVol] = useState(80);
  const [incomingToneSaving, setIncomingToneSaving] = useState(false);

  useEffect(() => {
    const profile = loadCallSoundPreferences();
    const hasTc =
      callSettings &&
      Object.prototype.hasOwnProperty.call(callSettings, 'incoming_ringtone');
    setIncomingToneId(
      normalizeStoredRingtoneId(
        hasTc
          ? String(callSettings!.incoming_ringtone ?? 'classic')
          : profile.incomingRingtone,
      ),
    );
    setIncomingToneVol(
      hasTc && typeof callSettings!.incoming_ringtone_volume === 'number'
        ? Math.min(100, Math.max(0, callSettings!.incoming_ringtone_volume as number))
        : Math.round(profile.ringtoneVolume * 100),
    );
  }, [callSettings]);

  const previewIncomingRingtone = () => {
    const path = getRingtoneOption(incomingToneId).filePath;
    playCallSound(path, incomingToneVol / 100);
  };

  const saveIncomingRingtoneSection = async () => {
    if (!onSaveIncomingRingtone) return;
    setIncomingToneSaving(true);
    try {
      await onSaveIncomingRingtone(incomingToneId, incomingToneVol);
    } catch (e) {
      console.error('[CallSettingsPanel] save ringtone:', e);
      toast.error(e instanceof Error ? e.message : 'Failed to save incoming ringtone');
    } finally {
      setIncomingToneSaving(false);
    }
  };

  const clearIncomingRingtoneSection = async () => {
    if (!onClearIncomingRingtone) return;
    setIncomingToneSaving(true);
    try {
      await onClearIncomingRingtone();
    } catch (e) {
      console.error('[CallSettingsPanel] clear ringtone:', e);
      toast.error(e instanceof Error ? e.message : 'Failed to reset ringtone preference');
    } finally {
      setIncomingToneSaving(false);
    }
  };

  const shellSurface = isDark ? 'bg-gray-900' : 'bg-white';
  const headerBorder = isDark ? 'border-gray-800' : 'border-gray-200';

  const panel = (
    <>
    <div
      className={`call-settings-portal-root fixed top-0 right-0 bottom-0 left-0 z-[100] flex w-full flex-col overflow-hidden shadow-2xl animate-slide-in-right sm:left-auto sm:max-w-2xl ${shellSurface}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="call-settings-title"
      style={{
        paddingTop: MOBILE_SAFE_AREA_TOP,
        paddingBottom: MOBILE_SAFE_AREA_BOTTOM,
      }}
    >
        {/* Header — match TotalConnectRtcIdleShell + CapacitorModal density */}
        <div
          className={`flex flex-shrink-0 items-center justify-between border-b px-4 py-3 ${headerBorder}`}
        >
          <h3
            id="call-settings-title"
            className={`flex items-center gap-2 text-lg font-semibold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            <Settings
              className="h-5 w-5 flex-shrink-0"
              style={accentColor ? { color: accentColor } : undefined}
            />
            Call Settings
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
            }`}
          >
            <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>
        
        {/* Content */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {/* DND Section */}
          <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDNDEnabled ? (
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <BellOff className="w-5 h-5 text-red-400" />
                  </div>
                ) : (
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <Bell className="w-5 h-5 text-green-400" />
                  </div>
                )}
                <div>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Do Not Disturb
                  </p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isDNDEnabled ? 'Calls will be silenced' : 'Receive all calls'}
                  </p>
                </div>
              </div>
              <button
                onClick={onToggleDND}
                disabled={dndLoading}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  isDNDEnabled ? 'bg-red-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                } ${dndLoading ? 'opacity-50' : ''}`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    isDNDEnabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {hasSipExtension && onToggleSipInboundMobileOnly && (
            <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      sipInboundMobileOnly ? 'bg-blue-500/20' : isDark ? 'bg-gray-600/50' : 'bg-gray-200'
                    }`}
                  >
                    <Smartphone
                      className={`w-5 h-5 ${
                        sipInboundMobileOnly ? 'text-blue-400' : isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  </div>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Ring inbound SIP on mobile only
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {sipInboundMobileOnly
                        ? 'Portal and desktop will not ring; use your phone app'
                        : 'All signed-in devices can receive inbound SIP'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onToggleSipInboundMobileOnly}
                  disabled={sipInboundMobileOnlyLoading}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    sipInboundMobileOnly
                      ? 'bg-blue-500'
                      : isDark
                        ? 'bg-gray-600'
                        : 'bg-gray-300'
                  } ${sipInboundMobileOnlyLoading ? 'opacity-50' : ''}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      sipInboundMobileOnly ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {onSaveIncomingRingtone && (
            <div
              className={`rounded-xl p-4 space-y-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Volume2
                  className="w-4 h-4 text-violet-400"
                  style={accentColor ? { color: accentColor } : undefined}
                />
                <span
                  className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  Incoming ringtone
                </span>
              </div>
              <p
                className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              >
                Sound for Total Connect and SIP calls in this app (web, Tauri desktop,
                mobile). Syncs with your account. If you have not saved here yet, your{' '}
                <span className="font-medium">Client Zone → Profile</span> ringtone is used.
              </p>
              <div className="space-y-2">
                <label
                  className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Sound
                </label>
                <select
                  value={incomingToneId}
                  onChange={(e) => setIncomingToneId(e.target.value)}
                  disabled={incomingToneSaving}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-800 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  {CALL_RINGTONE_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span
                    className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    Volume
                  </span>
                  <span
                    className={`text-xs tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {incomingToneVol}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={incomingToneVol}
                  onChange={(e) => setIncomingToneVol(parseInt(e.target.value, 10))}
                  disabled={incomingToneSaving}
                  className="w-full"
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={previewIncomingRingtone}
                  disabled={incomingToneSaving}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    isDark
                      ? 'bg-gray-600 text-white hover:bg-gray-500'
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  }`}
                >
                  Preview
                </button>
                <button
                  type="button"
                  onClick={() => void saveIncomingRingtoneSection()}
                  disabled={incomingToneSaving}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: accentColor || '#7c3aed' }}
                >
                  {incomingToneSaving ? 'Saving…' : 'Save'}
                </button>
                {onClearIncomingRingtone ? (
                  <button
                    type="button"
                    onClick={() => void clearIncomingRingtoneSection()}
                    disabled={incomingToneSaving}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      isDark
                        ? 'text-violet-300 hover:bg-gray-600/80'
                        : 'text-violet-700 hover:bg-violet-50'
                    }`}
                  >
                    Use profile default
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {/* Voicemail */}
          {onOpenVoicemail && (
            <button
              onClick={() => {
                onClose();
                onOpenVoicemail();
              }}
              className={`w-full rounded-xl p-4 flex items-center gap-3 transition-colors ${isDark ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}
            >
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Voicemail className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Voicemail</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Listen to voice messages</p>
              </div>
            </button>
          )}

          {/* Speed Dials Section */}
          <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Speed Dials
                </span>
              </div>
            </div>
            
            {callSettings?.speed_dials && callSettings.speed_dials.length > 0 ? (
              <div className="space-y-2">
                {callSettings.speed_dials.map((sd) => (
                  <button
                    key={sd.id}
                    onClick={() => onDialSpeedDial?.(sd.target_value)}
                    className={`w-full flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${
                      isDark
                        ? 'bg-gray-600/50 hover:bg-gray-600'
                        : 'bg-white hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-400 font-mono font-bold">
                        {sd.speed_dial_code}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {sd.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {sd.target_type === 'extension' ? `Ext. ${sd.target_value}` : sd.target_value}
                      </span>
                      <Phone className="w-3.5 h-3.5 text-green-400" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                No speed dials configured. Use *XX to quick dial.
              </p>
            )}
          </div>

          {/* Call Forwarding Info */}
          <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <PhoneForwarded className="w-4 h-4 text-blue-400" />
              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Call Forwarding
              </span>
            </div>
            {callSettings?.forward_rules ? (
              <div className="space-y-1 text-sm">
                {callSettings.forward_rules.always_forward?.enabled && (
                  <p className="text-green-400">
                    Always forward to: {callSettings.forward_rules.always_forward.target}
                  </p>
                )}
                {callSettings.forward_rules.busy_forward?.enabled && (
                  <p className="text-yellow-400">
                    When busy: {callSettings.forward_rules.busy_forward.target}
                  </p>
                )}
                {callSettings.forward_rules.no_answer_forward?.enabled && (
                  <p className="text-orange-400">
                    No answer: {callSettings.forward_rules.no_answer_forward.target}
                  </p>
                )}
                {!callSettings.forward_rules.always_forward?.enabled && 
                 !callSettings.forward_rules.busy_forward?.enabled && 
                 !callSettings.forward_rules.no_answer_forward?.enabled && (
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    No forwarding rules active
                  </p>
                )}
              </div>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Configure in PBX Settings
              </p>
            )}
          </div>

          {/* Call Preferences */}
          <div className={`rounded-xl p-4 space-y-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-4 h-4 text-purple-400" />
              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Preferences
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Call Waiting
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                callSettings?.call_waiting !== false 
                  ? 'bg-green-500/20 text-green-400' 
                  : isDark ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>
                {callSettings?.call_waiting !== false ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            
            {callSettings?.auto_answer_seconds && callSettings.auto_answer_seconds > 0 && (
              <div className="flex items-center justify-between">
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Auto Answer
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                  After {callSettings.auto_answer_seconds}s
                </span>
              </div>
            )}

            {callSettings?.sip_extension && (
              <div className="flex items-center justify-between">
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Extension
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                  {callSettings.sip_extension.extension_number} ({callSettings.sip_extension.display_name})
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className={`flex flex-shrink-0 items-center border-t px-4 py-3 ${headerBorder}`}>
          <p className={`w-full text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Advanced PBX settings are in the PABX module. Incoming ringtone above applies to Total Connect on this app.
          </p>
        </div>

    </div>
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .call-settings-portal-root.animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(panel, document.body);
}

export default CallSettingsPanel;
